// react
import { useCallback } from 'react';
import { redirect } from 'react-router-dom';

// context
import { useTenant, AccountRole, Feature, Permission } from 'context';

/**
 * useRouterGuard provides functions to guard routes based on tenant state.
 */
export const useRouterGuard = () => {
  // hooks
  const { tenant } = useTenant();
  const { session, accountRole, organizationTypeId, features, permissions } =
    tenant;
  const hasBookCall = permissions.includes(Permission.BOOK_CALL);

  // methods
  /**
   * buildLoader builds a react-router loader function from a boolean value..
   */
  const buildLoader = useCallback(
    (isAllow: boolean, redirectPath?: string) => {
      // default redirect path based on account role
      const defaultRedirectPaths: Record<AccountRole, string> = {
        [AccountRole.ADMIN]: '/admin',
        [AccountRole.ORG_OWNER]: '/portal',
        [AccountRole.MEMBER]: '/',
        [AccountRole.PARTNER_ADMIN]: '/partner-admin',
        [AccountRole.NONE]: '/sign-in',
      };
      if (redirectPath === undefined) {
        redirectPath = defaultRedirectPaths[accountRole] ?? '/';
      }
      const loader = async () => {
        if (!isAllow) throw redirect(redirectPath || '/');
        return null;
      };
      (loader as any)._allow = isAllow;
      return loader;
    },
    [accountRole],
  );

  /**
   * requireAuth checks if the tenant is authenticated or not.
   */
  const requireAuth = useCallback(
    (isAuth: boolean) => isAuth === session.isAuthenticated,
    [session.isAuthenticated],
  );

  /**
   * requireVerified checks if the tenant is verified or not.
   */
  const requireVerified = useCallback(
    (isVerified: boolean) => isVerified === session.isVerified,
    [session.isVerified],
  );

  /**
   * requireOnboarded checks if the tenant is onboarded or not.
   */
  const requireOnboarded = useCallback(
    (isOnboarded: boolean) => isOnboarded === session.isOnboarded,
    [session.isOnboarded],
  );

  /**
   * requireRoles checks if the tenant has one of the required roles.
   */
  const requireRoles = useCallback(
    (...roles: AccountRole[]) => roles.includes(accountRole),
    [accountRole],
  );

  /**
   * requireFeatures checks if the tenant has at least one of the required
   * features.
   */
  const requireFeatures = useCallback(
    (...f: Feature[]) => f.some((feature) => features.includes(feature)),
    [features],
  );

  /**
   * requirePermissions checks if the tenant has at least one of the required
   * permissions.
   */
  const requirePermissions = useCallback(
    (...p: Permission[]) =>
      p.some((perm) => {
        if (perm === Permission.BOOK_CALL) return hasBookCall;
        return false;
      }),
    [hasBookCall],
  );

  /**
   * requireOrganizationType checks if the tenant has one of the required
   * organization types.
   */
  const requireOrganizationType = useCallback(
    (...orgTypes: string[]) => orgTypes.includes(organizationTypeId),
    [organizationTypeId],
  );

  /**
   * requireReferringPartner checks if the tenant is a referring partner or not.
   */
  const requireReferringPartner = useCallback(
    (isReferringPartner: boolean) =>
      isReferringPartner === session.isReferringPartner,
    [session.isReferringPartner],
  );

  return {
    requireAuth,
    requireOnboarded,
    requireRoles,
    requireFeatures,
    requirePermissions,
    requireOrganizationType,
    requireVerified,
    requireReferringPartner,
    buildLoader,
  };
};
